<template>
	<div class="file-preview mt-2 w-full">
		<DialogTitle class="sr-only">File Preview</DialogTitle>
		<DialogDescription class="sr-only">
			<p class="text-center">Media player of selected file.</p>
		</DialogDescription>
		<div class="overflow-hidden rounded-md p-4">
			<WaveSurferPlayer
				v-if="mediaLoaded"
				:options="getWaveSurferOptions()"
				@timeupdate="timeUpdateHandler"
				@ready="readyHandler"
				@wave-surfer="readyWaveSurferHandler"
			/>
			<div v-else-if="errorLoading" class="text-center text-red-500">
				<p>There was an error loading the media.</p>
			</div>
			<div v-else class="loader mx-auto size-11 py-0.5"></div>
		</div>
		<div v-if="mediaLoaded" class="flex items-center justify-center gap-4 pt-2">
			<div class="flex items-center gap-1">
				<button class="jmiBtn w-4 border-0 bg-transparent" @click.stop="downloadFile">
					<span class="fa-light fa-download"></span>
				</button>
				<button class="jmiBtn w-4 border-0 bg-transparent" @click.stop="togglePlayPause">
					<span :class="{ 'fa-light fa-pause': isPlaying, 'fa-light fa-play': !isPlaying }"></span>
				</button>
			</div>
			<p class="pb-1">
				<strong>{{ currentTime }}</strong> / {{ totalDuration }}
			</p>
		</div>
	</div>
</template>

<script>
import { DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { WaveSurferPlayer } from '@meersagor/wavesurfer-vue'
import filesApi from '@/scripts/files/files.api'
import cdrApi from '@/scripts/calldetailrecords/calldetailrecords.api'
import voicemailMessagesApi from '@/scripts/voicemailmessages/voicemailmessages.api'

export default {
	components: {
		WaveSurferPlayer,
		DialogTitle,
		DialogDescription
	},
	props: {
		file: {
			type: [Object, Blob],
			required: true
		},
		type: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			mediaLoaded: false,
			fileBlob: null,
			options: {
				height: 46,
				waveColor: 'gray',
				progressColor: 'green',
				barGap: 0,
				barWidth: 2,
				barRadius: 0,
				duration: 10
			},
			currentTime: '00:00',
			totalDuration: '00:00',
			isPlaying: false,
			waveSurfer: null,
			errorLoading: false
		}
	},
	watch: {
		file: {
			handler() {
				this.fetchFileBlob()
			},
			immediate: true
		}
	},
	methods: {
		async togglePlayPause() {
			if (this.waveSurfer) {
				try {
					const playPromise = this.waveSurfer.playPause()
					if (playPromise !== undefined) {
						await playPromise
						this.isPlaying = !this.isPlaying
					}
				} catch (error) {
					console.error('Playback error:', error)
				}
			}
		},
		async readyWaveSurferHandler(ws) {
			this.waveSurfer = ws
			this.waveSurfer.loadBlob(this.fileBlob)
		},
		downloadFile() {
			if (this.fileBlob) {
				const url = window.URL.createObjectURL(this.fileBlob)
				const a = document.createElement('a')
				a.style.display = 'none'
				a.href = url
				a.download = this.file?.name || 'TeleFlow Recording'
				document.body.appendChild(a)
				a.click()
				window.URL.revokeObjectURL(url)
				document.body.removeChild(a)
			}
		},
		getWaveSurferOptions() {
			return {
				...this.options
			}
		},
		formatTime(seconds) {
			return [seconds / 60, seconds % 60].map((v) => `0${Math.floor(v)}`.slice(-2)).join(':')
		},
		timeUpdateHandler(time) {
			this.currentTime = this.formatTime(time)
		},
		readyHandler(duration) {
			this.totalDuration = this.formatTime(duration)
		},
		async fetchFileBlob() {
			if (this.file instanceof Blob) {
				this.fileBlob = this.file
				this.mediaLoaded = true
			} else if (this.file && this.file.id) {
				try {
					let response
					if (this.type === 'cdr') {
						console.log('fetching cdr', this.file)
						response = await cdrApi.downloadRecordingById(this.file.id)
					} else if (this.type === 'voicemail') {
						response = await voicemailMessagesApi.downloadById(this.file.voicemail_id)
					} else {
						response = await filesApi.downloadById(this.file.id)
					}
					this.fileBlob = new Blob([response])
					this.mediaLoaded = true
				} catch (error) {
					this.errorLoading = true
					console.error(error)
				}
			}
		}
	}
}
</script>
